import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const statusLabels = { pending: 'Bekliyor', approved: 'Onaylandi', rejected: 'Reddedildi', paid: 'Odendi' };
const statusColors = { pending: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', rejected: 'bg-[#7a3535]/10 text-[#7a3535]', paid: 'bg-[#2e2d29]/10 text-[#2e2d29]' };

export default function AdvanceList() {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ employee_id: '', amount: '', currency: 'TRY', description: '' });
  const [loading, setLoading] = useState(false);

  const fetch = () => api.get('/hr/advances').then(r => setRecords(r.data)).catch(() => {});
  useEffect(() => { fetch(); api.get('/hr/employees').then(r => setEmployees(r.data)).catch(() => {}); }, []);

  const handleCreate = async () => {
    setLoading(true);
    try { await api.post('/hr/advances', form); toast.success('Kaydedildi'); setShowNew(false); fetch(); }
    catch (err) { toast.error('Hata'); }
    setLoading(false);
  };

  const approve = async (id) => { await api.patch(`/hr/advances/${id}/approve`); toast.success('Onaylandi'); fetch(); };
  const reject = async (id) => { await api.patch(`/hr/advances/${id}/reject`); toast.success('Reddedildi'); fetch(); };

  return (
    <div data-testid="advances-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Avanslar</h1>
        <Button onClick={() => setShowNew(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"><Plus size={16} className="mr-2" /> Yeni Avans</Button>
      </div>
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Personel</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Aciklama</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islem</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {records.map(r => (
              <TableRow key={r.id} className="erp-table-row border-[#d0c4b0]">
                <TableCell className="font-medium text-[#1a1916]">{r.employee_name}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{r.request_date}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{r.amount?.toFixed(2)} {r.currency}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{r.description || '-'}</TableCell>
                <TableCell><span className={`status-badge ${statusColors[r.status]}`}>{statusLabels[r.status]}</span></TableCell>
                <TableCell className="text-right">
                  {r.status === 'pending' && (<div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => approve(r.id)} className="text-[#3d6e4e] h-7"><Check size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => reject(r.id)} className="text-[#7a3535] h-7"><X size={14} /></Button>
                  </div>)}
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">Kayit yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Avans Talebi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Personel *</Label>
              <Select value={form.employee_id || undefined} onValueChange={(v) => setForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue placeholder="Sec" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tutar *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-2"><Label>PB</Label>
                <Select value={form.currency} onValueChange={(v) => setForm(p => ({ ...p, currency: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2"><Label>Aciklama</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleCreate} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
